import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const FollowUpsPage = () => {
  const user = getUser();
  const nav = useNavigate();
  const allowed = hasAnyRole(user?.role || "", FOLLOW_UP_ROLES);
  const qc = useQueryClient();
  const { t, show } = useToast();
  const [tab, setTab] = useState("My Tasks");
  const [completing, setCompleting] = useState("");
  const [page, setPage] = useState(1);
  const [ownerFilter, setOwnerFilter] = useState(() => localStorage.getItem("cc-followups-owner") || "");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("cc-followups-sort") || "due-asc");
  const [dateFrom, setDateFrom] = useState(() => localStorage.getItem("cc-followups-date-from") || "");
  const [dateTo, setDateTo] = useState(() => localStorage.getItem("cc-followups-date-to") || "");
  const managerView = !["SALES"].includes(user?.role || "");
  const scope = managerView ? {} : { mine: "true" };
  const { data: openTasks = [], isLoading: openLoading, error: openError } = useFollowUpTasks({ status: "OPEN", ...scope }, allowed);
  const { data: completedTasks = [], isLoading: completedLoading } = useFollowUpTasks({ status: "COMPLETED", ...scope }, allowed && tab === "Completed");
  const now = new Date();
  const start = new Date(startOfDayIso());
  const end = new Date(endOfDayIso());
  const overdue = (openTasks || []).filter((f: any) => new Date(f.dueAt) < now);
  const dueToday = (openTasks || []).filter((f: any) => { const d = new Date(f.dueAt); return d >= start && d <= end; });
  const upcoming = (openTasks || []).filter((f: any) => new Date(f.dueAt) > end);
  const mine = (openTasks || []).filter((f: any) => f.assignedTo?.id === user?.id || !managerView);
  const tabs = managerView ? ["My Tasks", "Team Tasks", "Overdue", "Today", "Upcoming", "Completed"] : ["My Tasks", "Overdue", "Today", "Upcoming", "Completed"];
  const lists: any = { "My Tasks": mine, "Team Tasks": openTasks, Overdue: overdue, Today: dueToday, Upcoming: upcoming, Completed: completedTasks };
  const rows = lists[tab] || mine;
  const loading = openLoading || (tab === "Completed" && completedLoading);

  const owners = Array.from(
    new Map(
      [...openTasks, ...completedTasks]
        .filter((task: any) => task.assignedTo?.id)
        .map((task: any) => [
          task.assignedTo.id,
          task.assignedTo,
        ]),
    ).values(),
  ) as any[];

  useEffect(() => {
    localStorage.setItem("cc-followups-owner", ownerFilter);
    localStorage.setItem("cc-followups-sort", sortBy);
    localStorage.setItem("cc-followups-date-from", dateFrom);
    localStorage.setItem("cc-followups-date-to", dateTo);
    setPage(1);
  }, [ownerFilter, sortBy, dateFrom, dateTo]);

  const filteredRows = [...rows]
    .filter((task: any) => {
      if (ownerFilter && task.assignedTo?.id !== ownerFilter) {
        return false;
      }

      const due = new Date(task.dueAt).getTime();

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`).getTime();
        if (due < from) return false;
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`).getTime();
        if (due > to) return false;
      }

      return true;
    })
    .sort((a: any, b: any) => {
      if (sortBy === "due-desc") {
        return new Date(b.dueAt).getTime() -
          new Date(a.dueAt).getTime();
      }

      if (sortBy === "customer") {
        const aName =
          a.inquiry?.customerName ||
          a.customer?.name ||
          "";

        const bName =
          b.inquiry?.customerName ||
          b.customer?.name ||
          "";

        return String(aName).localeCompare(String(bName));
      }

      if (sortBy === "owner") {
        return String(a.assignedTo?.name || "").localeCompare(
          String(b.assignedTo?.name || ""),
        );
      }

      return new Date(a.dueAt).getTime() -
        new Date(b.dueAt).getTime();
    });

  const exportFollowUpView = () => {
    const rowsForCsv = [
      ["Task", "Customer", "Owner", "Due", "Status"],
      ...filteredRows.map((task: any) => [
        task.title || "",
        task.inquiry?.customerName ||
          task.customer?.name ||
          "",
        task.assignedTo?.name || "Unassigned",
        task.dueAt || "",
        task.status || "",
      ]),
    ];

    const csv = rowsForCsv
      .map((row) =>
        row
          .map((value) =>
            `"${String(value).replace(/"/g, '""')}"`
          )
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `followups-view-${todayStr()}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
  };

  const resetFollowUpView = () => {
    setOwnerFilter("");
    setSortBy("due-asc");
    setDateFrom("");
    setDateTo("");

    localStorage.removeItem("cc-followups-owner");
    localStorage.removeItem("cc-followups-sort");
    localStorage.removeItem("cc-followups-date-from");
    localStorage.removeItem("cc-followups-date-to");
  };

  const pageSize = 25;
  const pageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / pageSize),
  );
  const safePage = Math.min(page, pageCount);
  const visibleRows = filteredRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  const complete = async (task: any) => {
    const ok = window.confirm(`Complete follow-up "${task.title}"? Confirm only after the customer was actually contacted.`);
    if (!ok) return;
    setCompleting(task.id);
    try {
      await http.patch(`/follow-up-tasks/${task.id}/complete`, { notes: task.notes || undefined });
      show("Follow-up completed");
      qc.invalidateQueries({ queryKey: ["follow-up-tasks"] });
    } catch (e: any) {
      show(getErr(e), "error");
    } finally {
      setCompleting("");
    }
  };

  if (!allowed) {
    return <Card><EmptyState icon="shield-check" title="Follow-ups are restricted" description="This workbench is available to owners, admins, operations managers, sales managers, and sales teammates." /></Card>;
  }

  const dueText = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div>
      {t && <Toast {...t} onClose={() => {}} />}
      <PageHeader
        eyebrow="Sales execution"
        title="Follow-up Workbench"
        subtitle="Human-owned customer follow-ups only. Work overdue tasks first, then today, then upcoming. No auto-follow-up, campaign reminder, or WhatsApp auto-send is triggered here."
        actions={<StatusPill tone={managerView ? "info" : "neutral"}>{managerView ? "Team visibility" : "My queue"}</StatusPill>}
      />

      {overdue.length > 0 && <AlertCard tone="danger" title={`${overdue.length} overdue follow-up${overdue.length > 1 ? "s" : ""}`}>Clear these before new outreach. Complete only after a real customer contact.</AlertCard>}

      <div className="op-grid-4" style={{ marginBottom: 16 }}>
        <MetricCard label="Open" value={openLoading ? "…" : openTasks.length} tone={C.primary} icon="clock" detail="Total active tasks" />
        <MetricCard label="Overdue" value={openLoading ? "…" : overdue.length} tone={overdue.length ? C.danger : C.success} icon="clock-alert" detail="Highest priority" />
        <MetricCard label="Due Today" value={openLoading ? "…" : dueToday.length} tone={C.warning} icon="calendar-check" detail="Must be worked today" />
        <MetricCard label="Upcoming" value={openLoading ? "…" : upcoming.length} tone={C.info} icon="route" detail="Next queue" />
      </div>

      <Tabs
        items={tabs}
        active={tab}
        onChange={(nextTab: string) => {
          setTab(nextTab);
          setPage(1);
        }}
      />

      <div className="op-filterbar" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as any }}>
          {managerView && (
            <select
              aria-label="Follow-up owner"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              style={{ padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
            >
              <option value="">All owners</option>
              {owners.map((owner: any) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </select>
          )}

          <input
            aria-label="Follow-up date from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ padding: "8px 10px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
          />

          <input
            aria-label="Follow-up date to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ padding: "8px 10px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
          />

          <select
            aria-label="Sort follow-ups"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
          >
            <option value="due-asc">Due · earliest first</option>
            <option value="due-desc">Due · latest first</option>
            <option value="customer">Customer · A–Z</option>
            <option value="owner">Owner · A–Z</option>
          </select>

          <Btn size="sm" v="ghost" onClick={resetFollowUpView}>
            Reset View
          </Btn>

          <Btn size="sm" v="secondary" onClick={exportFollowUpView}>
            Export Current View
          </Btn>
        </div>
      </div>

      {openError ? <Card><ErrorState title="Follow-ups could not load" description={getErr(openError)} /></Card> :
        loading ? <LoadingState rows={3} /> :
          !filteredRows.length ? <Card><EmptyState icon="check" title="No follow-ups here" description="This queue is clear. Keep conversation updates current as new inquiries arrive." /></Card> :
            <>
              <div style={{ display: "grid", gap: 12 }}>
                {visibleRows.map((task: any) => {
                  const ref = task.inquiry?.inquiryNumber || task.customer?.name || "Customer follow-up";
                  const customer = task.inquiry?.customerName || task.customer?.name || "Unknown customer";
                  const phone = task.inquiry?.phone || task.customer?.phone || "";
                  const late = new Date(task.dueAt) < now && task.status === "OPEN";
                  const today = (() => { const d = new Date(task.dueAt); return d >= start && d <= end; })();
                  return (
                    <Card key={task.id} urgency={late ? C.danger : urgColor(task.dueAt)} style={{ padding: 18, background: late ? "#FFF7F7" : C.surface }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" as any }}>
                        <div style={{ flex: 1, minWidth: 260 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" as any }}>
                            <StatusPill tone={late ? "danger" : today ? "warning" : "info"}>{late ? "Overdue" : today ? "Due today" : urgLabel(task.dueAt)}</StatusPill>
                            <Badge color={task.status === "COMPLETED" ? C.success : C.warning}>{task.status}</Badge>
                            {task.inquiry?.status && <StatusBadge status={task.inquiry.status} />}
                          </div>
                          <div style={{ fontSize: 17, fontWeight: 900, color: C.text }}>{task.title}</div>
                          <div style={{ fontSize: 13, color: C.mid, marginTop: 5, lineHeight: 1.55 }}>{task.notes || "No notes recorded yet."}</div>
                        </div>
                        <div style={{ minWidth: 230, display: "grid", gap: 7, fontSize: 12, color: C.mid }}>
                          <div><strong style={{ color: C.text }}>Customer:</strong> {customer}</div>
                          <div><strong style={{ color: C.text }}>Reference:</strong> {ref}</div>
                          <div><strong style={{ color: C.text }}>Due:</strong> {dueText(task.dueAt)}</div>
                          <div><strong style={{ color: C.text }}>Owner:</strong> {task.assignedTo?.name || "Unassigned"}</div>
                          {task.createdBy?.name && <div><strong style={{ color: C.text }}>Created by:</strong> {task.createdBy.name}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as any, justifyContent: "flex-end" }}>
                          {phone && <ActionButton icon="phone" label="Call" onClick={() => openExternal(telUrl(phone))} />}
                          {phone && <ActionButton icon="message-circle" label="WhatsApp" onClick={() => openExternal(waUrl(phone))} />}
                          <ActionButton icon="clipboard-list" label={task.inquiry ? "Open Inquiry" : "Open CRM"} onClick={() => nav(task.inquiry ? "/orders" : "/customers")} />
                          {task.status === "OPEN" && <Btn size="sm" v="success" loading={completing === task.id} onClick={() => complete(task)}>Complete</Btn>}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {filteredRows.length > pageSize && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                  <Btn
                    size="sm"
                    v="ghost"
                    disabled={safePage <= 1}
                    onClick={() => setPage(Math.max(1, safePage - 1))}
                  >
                    Previous
                  </Btn>

                  <span style={{ fontSize: 12, color: C.mid }}>
                    Page {safePage} of {pageCount} · {filteredRows.length} matching tasks
                  </span>

                  <Btn
                    size="sm"
                    v="ghost"
                    disabled={safePage >= pageCount}
                    onClick={() => setPage(Math.min(pageCount, safePage + 1))}
                  >
                    Next
                  </Btn>
                </div>
              )}
            </>
      }
    </div>
  );
};

export default FollowUpsPage;
